import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { parseQrAttendancePayload } from '../utils/qrHelpers';
import './QrScannerModal.css';

const QrScannerModal = ({ isOpen, onClose, onScan, videoRef }) => {
  const scannerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  const processingRef = useRef(false);
  const fileInputRef = useRef(null);
  const regionId = 'helport-qr-reader';
  const [scannerError, setScannerError] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const lastScanRef = useRef('');

  onScanRef.current = onScan;
  onCloseRef.current = onClose;

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      await scanner.stop();
      scanner.clear();
    } catch (err) {
      // Scanner may already be stopped.
    }
    scannerRef.current = null;
  }, []);

  const resumeMainCamera = useCallback(async (videoElement) => {
    if (!videoElement) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      videoElement.srcObject = stream;
      await videoElement.play();
    } catch (err) {
      console.error('Camera resume error:', err);
    }
  }, []);

  const processDecodedText = useCallback(async (decodedText) => {
    if (processingRef.current) return false;

    const uid = parseQrAttendancePayload(decodedText);
    if (!uid) {
      setScannerError('Invalid QR code. Please use a Helport employee QR badge.');
      return false;
    }

    if (lastScanRef.current === uid) return false;

    processingRef.current = true;
    lastScanRef.current = uid;
    setIsProcessing(true);
    setScannerError('');

    await stopScanner();
    onScanRef.current(decodedText);
    onCloseRef.current();
    return true;
  }, [stopScanner]);

  const handleClose = useCallback(async () => {
    processingRef.current = false;
    await stopScanner();
    await resumeMainCamera(videoRef?.current);
    onClose();
  }, [onClose, resumeMainCamera, stopScanner, videoRef]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || processingRef.current) return;

    setScannerError('');
    setIsProcessing(true);
    processingRef.current = true;

    try {
      const fileScanner = new Html5Qrcode(regionId, { verbose: false });
      const decodedText = await fileScanner.scanFile(file, false);
      fileScanner.clear();
      processingRef.current = false;
      setIsProcessing(false);
      await processDecodedText(decodedText);
    } catch (err) {
      processingRef.current = false;
      setIsProcessing(false);
      setScannerError('Could not read QR from this image. Try a clearer photo or download again.');
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    const videoElement = videoRef?.current;

    setScannerError('');
    setIsStarting(true);
    setIsProcessing(false);
    processingRef.current = false;
    lastScanRef.current = '';

    const pauseMainCamera = () => {
      const stream = videoElement?.srcObject;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (videoElement) {
        videoElement.srcObject = null;
      }
    };

    const startScanner = async () => {
      pauseMainCamera();
      await new Promise((resolve) => setTimeout(resolve, 220));

      try {
        const scanner = new Html5Qrcode(regionId, { verbose: false });
        scannerRef.current = scanner;

        const cameras = await Html5Qrcode.getCameras();
        if (cancelled) return;

        if (!cameras || cameras.length === 0) {
          setScannerError('No camera found. You can upload the downloaded QR image instead.');
          setIsStarting(false);
          return;
        }

        const preferredCamera = cameras.find((cam) => /back|rear|environment/i.test(cam.label));
        const cameraId = preferredCamera?.id || cameras[cameras.length - 1].id;

        await scanner.start(
          cameraId,
          {
            fps: 12,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.75;
              return { width: Math.floor(size), height: Math.floor(size) };
            },
            aspectRatio: 1,
          },
          async (decodedText) => {
            await processDecodedText(decodedText);
          },
          () => {}
        );

        if (!cancelled) setIsStarting(false);
      } catch (err) {
        if (!cancelled) {
          const message = String(err?.message || '');
          if (/NotReadableError|Could not start video source/i.test(message)) {
            setScannerError('Camera is busy. Please close other camera apps, then try again.');
          } else {
            setScannerError(message || 'Unable to start QR scanner. Try uploading the QR image instead.');
          }
          setIsStarting(false);
        }
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      processingRef.current = false;
      stopScanner().then(() => resumeMainCamera(videoElement));
    };
  }, [isOpen, processDecodedText, resumeMainCamera, stopScanner, videoRef]);

  if (!isOpen) return null;

  return (
    <div className="qr-scanner-overlay" onClick={handleClose}>
      <div className="qr-scanner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qr-scanner-header">
          <div>
            <h3>QR Backup Scan</h3>
            <p>Scan the downloaded employee QR badge to record attendance.</p>
          </div>
          <button type="button" className="qr-scanner-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div id={regionId} className="qr-scanner-region" />

        {isStarting && <p className="qr-scanner-status">Starting camera...</p>}
        {isProcessing && !isStarting && <p className="qr-scanner-status">Recording attendance...</p>}
        {scannerError && <p className="qr-scanner-error">{scannerError}</p>}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="qr-scanner-file-input"
          onChange={handleFileUpload}
        />

        <button
          type="button"
          className="qr-scanner-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing}
        >
          Upload Downloaded QR Image
        </button>

        <button type="button" className="qr-scanner-cancel-btn" onClick={handleClose}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default QrScannerModal;
